import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { SITE_NAME, SITE_LOGO_URL } from "@/mocks/seo";
import { useRouteLanguage, useLocalizedLinks } from "@/hooks/useLocale";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { pillarImages } from "@/mocks/images";

interface PillarSection {
  heading: string;
  paragraphs: string[];
  list?: string[];
}

interface PillarArticle {
  slug: string;
  title: string;
  description: string;
  category: string;
  updated: string;
  readTime: string;
  quickAnswer: string;
  sections: PillarSection[];
  faq: { q: string; a: string }[];
}

export default function Guide() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  useRouteLanguage();
  const { to: linkTo, lang } = useLocalizedLinks();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfanssheet.live";
  const articles = t("pillars.articles", { returnObjects: true }) as PillarArticle[];
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Header />
        <main className="bg-background-50 py-20">
          <div className="mx-auto w-full max-w-3xl px-4 text-center md:px-6">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("pillars.sectionTitle")}
            </h1>
            <p className="mt-4 text-sm text-foreground-700">{t("article.disclaimer")}</p>
            <Link
              to={linkTo("/")}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              {t("pillars.backHome")}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: pillarImages[article.slug] ?? pillarImages["usfans-review"],
    datePublished: "2026-08-20",
    dateModified: "2026-08-20",
    author: { "@type": "Organization", name: "USFansSheet Editorial Team" },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: SITE_LOGO_URL },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/${lang}/guides/${article.slug}`,
    },
    inLanguage: lang,
  };

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <Header />
      <main className="bg-background-50">
        <div className="mx-auto w-full max-w-4xl px-4 pt-6 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <Link to={linkTo("/")} className="transition-colors hover:text-primary-700">
              {t("article.breadcrumbHome")}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <Link to={linkTo("/#guides")} className="transition-colors hover:text-primary-700">
              {t("pillars.breadcrumbLabel")}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-900">{article.category}</span>
          </nav>
        </div>

        <article className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6">
          <div className="overflow-hidden rounded-lg">
            <img
              src={pillarImages[article.slug] ?? pillarImages["usfans-review"]}
              alt={article.title}
              title={article.title}
              className="h-56 w-full object-cover object-top md:h-80"
            />
          </div>

          <h1 className="mt-8 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-lg">{article.description}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-user-line text-sm"></i>
              </span>
              {t("article.meta.author")}
            </span>
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-time-line text-sm"></i>
              </span>
              {article.readTime}
            </span>
            <span>{t("article.meta.updated")} {article.updated}</span>
          </div>

          <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
            <span className="flex items-center gap-2 font-heading text-sm font-semibold text-primary-800">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-flashlight-line text-sm"></i>
              </span>
              {t("article.quickAnswerLabel")}
            </span>
            <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">{article.quickAnswer}</p>
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("article.tocLabel")}</h2>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {article.sections.map((section, i) => (
                <li key={section.heading}>
                  <a
                    href={`#section-${i}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </span>
                    {section.heading}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#guide-faq"
                  className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </span>
                  {t("article.sections.faq.title")}
                </a>
              </li>
            </ul>
          </div>

          {article.sections.map((section, i) => (
            <section key={section.heading} id={`section-${i}`} className="mt-10 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p} className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="mt-5 space-y-3">
                  {section.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-800 md:text-base">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                        <i className="ri-checkbox-circle-line text-base"></i>
                      </span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section id="guide-faq" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.faq.title")}
            </h2>
            <div className="mt-5 space-y-3">
              {article.faq.map((item) => (
                <div key={item.q} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{item.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                {t("article.sections.related.title")}
              </h2>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-6">
                <Link
                  to={linkTo("/")}
                  className="flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </span>
                  {t("pillars.backHome")}
                </Link>
                <Link
                  to={linkTo("/#safety")}
                  className="flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-shield-check-line text-sm"></i>
                  </span>
                  {t("article.sections.related.safetyText")}
                </Link>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-primary-950 p-6 text-center md:p-8">
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                {t("pillars.ctaTitle")}
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-background-100/80">{t("pillars.ctaText")}</p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                {t("pillars.ctaButton")}
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-arrow-right-up-line text-base"></i>
                </span>
              </a>
            </div>
          </section>

          <p className="mt-8 border-t border-background-200 pt-6 text-xs leading-relaxed text-foreground-600">
            {t("article.disclaimer")}
          </p>
        </article>
      </main>
      <Footer />
    </>
  );
}